import dom, { Fragment } from "../../dist/jsx-render/dom";
import { ComponentBase } from "./ComponentBase";
import { app } from "../Frame/App";

export type TextComponentProps = {
    field: string;
    fieldFallback?: string;
    model: any;
    saveChanges?: boolean;
    name?: string;
    showLabel?: boolean;
    required?: boolean;
    id?: string;
    maxLength?: number;
    disabled?: boolean;
    infoText?: string;

    attributes?: { [name: string]: string }

    /**
     * This event handler will be called when a key is pressed in the input/textarea, before the value is changed in the model.
     * @param event
     * @param changedValue
     * @returns
     */
    beforeChangeEvent?: (event: Event, changedValue?: string) => void;

    /** Pluggable external event handler. Use this to initiate 
     * actions in a parent component, when the value of this component changes. */
    changeEvent?: (event: Event, oldValule?: string) => void;

    /**
     * This event handler will be called, when the focus leaves the input.
     * @param event
     * @returns
     */
    onBlurEvent?: (event: Event, oldValue: string, newValue: string) => void;
    // TODO EnterHandler
    escapeHandler?: (event: Event) => void;
    enterHandler?: (event: Event) => void;

    children?: any;

    additionalCssClasses?: string;

    /** Set to "integer" or "decimal". */
    restrictTo?: string;
    /** Set a number of decimal places for a decimal input. */
    decimalPlaces?: number;
    /** Get this value from the Settings on the Server beforehand. If not set, it will default to a comma. */
    decimalSeparator?: number;

    /** Form group is created if null oder true. To not create a form group, set explicitly to false. */
    createFormGroup?: boolean;

    /** Create a multiline textbox. */
    textarea?: boolean;

    /** Number of lines for multiline textbox. */
    lines?: number;

    friendlyValueField?: string;
    inputType?: string;

    showTooltip?: boolean;

    placeholder?: string;
}


/**
 * Reusable Textbox component. Allows to add children, to use the bootstrap 
 * input-group-prepend feature. This adds additional info to the left side of the textbox.
 * Children have to be in the following format:
 * 
 * <div class="input-group-prepend">
 *   <div class="input-group-text">
 *     ...Some text or image...
 *   </div>
 * </div>
 * 
 * */
export class TextComponent extends ComponentBase {

    private beforeEditValue: string;
    private input: HTMLInputElement | HTMLTextAreaElement;

    constructor(props: TextComponentProps) {
        super(props);
        if (this.props.required == undefined)
            this.props.required = false;

        if (this.props.id) {
            this.clientId = this.props.id;
        } else {
            this.clientId = app.createGuid();
        }
    }
    clientId: string;
    props: TextComponentProps;
    element: HTMLElement;

    decimal: string = "decimal";
    integer: string = "integer";

    protected handleChange = (event: KeyboardEvent) => {
        let oldValue: string = null;
        if (this.props.model && this.props.field) {
            let oldValue = this.props.model[this.props.field];
        }

        let input = (event.target as HTMLInputElement);

        if (this.props.beforeChangeEvent) {
            this.props.beforeChangeEvent(event, input.value);
        }

        this.updateModelFromControl(input.value, this.props.field, this.props.model);

        // Call external event handler if present.
        if (this.props.changeEvent) {
            this.props.changeEvent(event, oldValue);
        }

        if (this.props.escapeHandler && event.key == "Escape") {
            this.props.escapeHandler(event);
        }

        if (this.props.enterHandler && event.key == "Enter") {
            this.props.enterHandler(event);
        }
    }

    protected handleBlur = (event: Event) => {
        if (this.input.value != this.beforeEditValue && this.props.onBlurEvent) {
            this.props.onBlurEvent(event, this.beforeEditValue, this.input.value);
        }

        // Set the new value, so the next time this controls blurs, we know if the value has changed or not.
        this.beforeEditValue = this.input.value;
    }

    protected handlePaste = (event: ClipboardEvent) => {
        let pastedText = event.clipboardData.getData('text');
        this.updateModelFromControl(pastedText, this.props.field, this.props.model);
    }

    handleRestriction = (event: KeyboardEvent) => {
        let input = event.target as HTMLInputElement;

        let charCode = (event.which) ? event.which : event.keyCode;
        if (this.props.restrictTo === this.integer) {
            // numbers and minus
            if ((charCode >= 48 && charCode <= 57) || charCode == 45) {
                event.returnValue = true;
            } else {
                event.returnValue = false;
            }
        } else if (this.props.restrictTo === this.decimal) {
            // numbers, minus and decimalpoints or commas
            if ((charCode >= 48 && charCode <= 57) || charCode == 45 || charCode == 44 || charCode == 46) {
                event.returnValue = true;
            } else {
                event.returnValue = false;
            }

            if (this.props.decimalPlaces) {
                if (app.exceedsDecimalPlaces(input.value, this.props.decimalPlaces, input.selectionStart)) {
                    event.returnValue = false;
                }
            }
        }

        event.returnValue = true;
    }

    private getLabelAndInput = () => {

        if (this.props.textarea) {
            this.input = <textarea id={this.clientId}
                value={this.props.model[this.props.field] ?? this.props.model[this.props.fieldFallback] ?? ""}
                className="form-control value"
                placeholder={this.props.placeholder ?? this.props.name ?? ""}
                rows={this.props.lines}
                title={this.props.showTooltip && this.props.name ? this.props.name : ""}
            />


        } else {
            this.input = <input type={this.props.inputType ?? "text"}
                id={this.clientId}
                value={this.props.model[this.props.field] ?? this.props.model[this.props.fieldFallback] ?? ""}
                className="form-control value"
                placeholder={this.props.placeholder ?? this.props.name ?? ""}
                title={this.props.showTooltip && this.props.name ? this.props.name : ""}
            />
        }

        if (this.props.attributes) {
            for (let key in this.props.attributes) {
                this.input.setAttribute(key, this.props.attributes[key]);
            }
        }

        return <Fragment>
            {!(this.props.showLabel === false) && this.props.name
                ? <label for={this.clientId} className="form-label">{this.props.name}</label>
                : []}
            {!(this.props.required === false) && !(this.props.showLabel === false)
                ? <span class="red required-mark">*</span>
                : []}
            {this.props.infoText
                ? <div className="text-component-info info-text">
                    {this.props.infoText}
                </div>
                : []}
            <div className={"input-group" + (this.props.createFormGroup === false ? ` ${this.props.additionalCssClasses ?? ""}` : "")}>
                {this.props.children}
                {this.input}
            </div>
        </Fragment>;
    }

    formatOnFocus = (event: Event) => {
        let input = event.target as HTMLInputElement;
        this.input.value = app.sanitizeDecimal(this.input.value, this.props.decimalSeparator);
    }

    formatOnBlur = (event: Event) => {
        let input = event.target as HTMLInputElement;
        // Update model value here also, because when pasting text, the paste-Event is not always called.
        this.updateModelFromControl(input.value, this.props.field, this.props.model);

        this.input.value = app.formatDecimalString(input.value, this.props.decimalSeparator, this.props.decimalPlaces);

        if (this.props.onBlurEvent) {
            this.handleBlur(event);
        }
    }

    render = () => {

        if (this.props.createFormGroup === false) {
            this.element = this.getLabelAndInput();
        } else {
            this.element =
                <div className={"form-group" + (this.props.additionalCssClasses ? " " + this.props.additionalCssClasses : "")}>
                    {this.getLabelAndInput()}
                </div>;
        }

        console.log(this.input);

        //let input = this.element.querySelector(this.props.textarea ? "textarea" : "input") as HTMLElement;

        if (!(this.props.saveChanges === false)) {
            this.input.addEventListener('keyup', this.handleChange);
            this.input.addEventListener('paste', this.handlePaste);
        }

        if (this.props.restrictTo != "decimal") {
            // if this is restricted to decimal, we should let the formatOnBlur take place, before the onBlurHandler is called.
            this.input.addEventListener("blur", this.handleBlur);
        }

        if (this.props.restrictTo) {
            this.input.addEventListener('keypress', this.handleRestriction);

            if (this.props.restrictTo === this.decimal) {
                this.input.addEventListener("focus", this.formatOnFocus);
                this.input.addEventListener("blur", this.formatOnBlur);
            }
        }

        if (this.props.maxLength) {
            this.input.setAttribute("maxlength", this.props.maxLength.toString());
        }

        if (this.props.lines && this.props.textarea) {
            let textArea = this.element.querySelector("textarea") as HTMLTextAreaElement;
            textArea.rows = this.props.lines;
        }

        if (this.props.model && this.props.field && this.props.model[this.props.field]) {
            if (this.props.textarea) {
                let textArea = this.element.querySelector("textarea") as HTMLTextAreaElement;
                textArea.value = this.props.model[this.props.field];
            } else {
                //let input = this.element.querySelector("input");

                if (this.props.restrictTo == this.decimal) {
                    this.input.value = app.formatDecimalNumber(this.props.model[this.props.field], this.props.decimalSeparator, this.props.decimalPlaces);
                } else {
                    this.input.value = this.props.model[this.props.field];
                }
            }

            //  set initial beforeEditValue
            this.beforeEditValue = this.props.model[this.props.field];
        }

        if (this.props.disabled) {
            let inputElement: HTMLInputElement | HTMLTextAreaElement = this.element.querySelector("input, textarea");
            if (inputElement) {
                inputElement.disabled = true;
            }
        }



        return this.element;
    }

    private updateModelFromControl = (value: string, field: string, model: any) => {
        let inputValue: any;
        if (this.props.restrictTo == this.integer) {
            model.setPropertyByPath(field, !isNaN(Number(value)) ? Number(value) : null);
            inputValue = !isNaN(Number(value)) ? Number(value) : null;
        } else if (this.props.restrictTo == this.decimal) {
            value = app.sanitizeDecimal(value, this.props.decimalSeparator);
            let floatValue = app.anyToFloat(value, null, this.props.decimalSeparator);
            model.setPropertyByPath(field, floatValue);
            inputValue = floatValue;
        } else {
            model.setPropertyByPath(field, value);
            inputValue = value;
        }

        if (this.props.friendlyValueField) {
            this.props.model.setPropertyByPath(this.props.friendlyValueField, inputValue);
        }
    }

    dispose() { }

    validate() { }
}